<script>

import {computed, onMounted, ref} from "vue";
import useValidation from "@/use/Validation.js";
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import {useCnasAgencyStore} from "@/stores/CnasAgency.js";
import useTools from "@/use/Tools.js";
import axios from "axios";

export default {

    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);
        const {mxRules} = useValidation();
        const {dialog, loading, overlay} = useSharedData();
        const {getBack} = useSharedMethod();
        const {$refs, $route} = useTools();
        const cnasAgency = ref({byDefault: false});
        const cnasAgencyStore = useCnasAgencyStore();

        function findById(id) {
            loading.value = true;
            axios.get(`/cnasAgencies/${id}`).then((response) => {
                cnasAgency.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/cnasAgencies', cnasAgency.value).then((response) => {
                    cnasAgencyStore.addCnasAgency(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/cnasAgencies', cnasAgency.value).then((response) => {
                    cnasAgencyStore.updateCnasAgency(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/cnasAgencies/${cnasAgency.value.id}`).then(() => {
                    cnasAgencyStore.deleteCnasAgency(cnasAgency.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            readonly,
            cnasAgencyStore,
            mxRules,
            dialog,
            loading,
            overlay,
            cnasAgency,
            save,
            update,
            remove,
            getBack,
        };
    },
};
</script>

<template>
        <v-dialog
            v-model="dialog"
            max-width="800"
            persistent
            scrollable
        >
            <v-card>
                <w-dialog-toolbar
                    :view-action="viewAction"
                    :delete-action="deleteAction"
                    :add-action="addAction"
                    :update-action="updateAction"
                ></w-dialog-toolbar>
                <w-overlay
                    :overlay="overlay"
                    :loading="loading"
                    :delete-action="deleteAction"
                    @remove="remove"
                    @cancel="overlay = false"
                ></w-overlay>
                <v-card-text>
                    <v-container fluid>
                        <v-form ref="form" lazy-validation>
                            <w-fieldset :title="$t('common.details')">
                                <v-row>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.code')" required/>
                                        <w-text-field
                                            v-model="cnasAgency.code"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.name')" required/>
                                        <w-text-field
                                            v-model="cnasAgency.name"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.agency_code')" required/>
                                        <w-text-field
                                            v-model="cnasAgency.agencyCode"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.membership_number')" required/>
                                        <w-text-field
                                            v-model="cnasAgency.membershipNumber"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.arabic_name')"/>
                                        <w-text-field
                                            v-model="cnasAgency.arabicName"
                                            :readonly="readonly"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.accounting_code')"/>
                                        <w-text-field
                                            v-model="cnasAgency.accountingCode"
                                            :readonly="readonly"
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="getBack"/>
                    <w-btn-add
                        :show="addAction"
                        :loading="loading"
                        @press="save(false)"
                    />
                    <w-btn-update
                        :show="updateAction"
                        :loading="loading"
                        @press="update"
                    />
                    <w-btn-delete
                        :show="deleteAction"
                        :loading="loading"
                        @press="overlay = true"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
</template>